import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Badge, Button, StatusDot } from '@/components/atoms';
import {
  useActivateTemplateVersion,
  useCreateTemplateVersion,
  useTemplate,
  useTestTemplate,
} from '@/hooks/useTemplates';
import type {
  ConfigurableField,
  TemplateDetail,
  TestResult,
} from '@/types/template';

import styles from './TemplateEditor.module.css';

export function TemplateEditor() {
  const params = useParams<{ id: string }>();
  const id = params.id!;
  const { data: template, isLoading } = useTemplate(id);

  const [configurable, setConfigurable] = useState<Record<string, unknown>>({});
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  const createVersionMutation = useCreateTemplateVersion();
  const activateVersionMutation = useActivateTemplateVersion();
  const testMutation = useTestTemplate();

  // Initialise configurable from active version
  useEffect(() => {
    if (!template) return;
    const activeVersion = template.versions.find(
      (v) => v.version_number === template.current_version_number,
    );
    setConfigurable(activeVersion?.configurable ?? {});
  }, [template]);

  if (isLoading) return <p>A carregar…</p>;
  if (!template) return <p>Não encontrado.</p>;

  const setField = (key: string, value: unknown) => {
    setConfigurable((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveVersion = async () => {
    setStatusMsg(null);
    try {
      const v = await createVersionMutation.mutateAsync({
        id,
        payload: { configurable },
      });
      setStatusMsg(`Nova versão criada (v${v.version_number}, draft).`);
    } catch (e) {
      setStatusMsg(`Erro: ${String(e)}`);
    }
  };

  const handleActivate = async (versionId: string, versionNumber: number) => {
    setStatusMsg(null);
    try {
      await activateVersionMutation.mutateAsync({ id, versionId });
      setStatusMsg(`v${versionNumber} activada.`);
    } catch (e) {
      setStatusMsg(`Erro: ${String(e)}`);
    }
  };

  const handleTest = async () => {
    setTestResult(null);
    setStatusMsg(null);
    try {
      const result = await testMutation.mutateAsync({
        id,
        payload: { configurable, sample_variables: {} },
      });
      setTestResult(result);
    } catch (e) {
      setStatusMsg(`Erro no teste: ${String(e)}`);
    }
  };

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <Link to="/config/prompts" className={styles.back}>
          ← Templates
        </Link>
        <h1 className={styles.title}>{template.name}</h1>
        {template.current_version_number !== null && (
          <Badge tone="info" size="sm">
            v{template.current_version_number} activa
          </Badge>
        )}
      </header>

      <p className={styles.warn}>
        Alterações criarão nova versão draft. Para aplicar a workflows, activar
        a versão e criar override (Axioma II).
      </p>

      <section className={styles.locked}>
        <h2 className={styles.sectionTitle}>System (locked)</h2>
        <pre className={styles.code}>{template.system_message}</pre>
      </section>

      <section className={styles.card}>
        <h2 className={styles.sectionTitle}>Configurável</h2>
        {Object.entries(template.configurable_schema).map(([key, spec]) => (
          <ConfigurableInput
            key={key}
            name={key}
            spec={spec}
            value={configurable[key]}
            onChange={(v) => setField(key, v)}
          />
        ))}
        {Object.keys(template.configurable_schema).length === 0 && (
          <p className={styles.muted}>
            Este template não tem campos configuráveis (embedding ou geração crua).
          </p>
        )}
      </section>

      <section className={styles.locked}>
        <h2 className={styles.sectionTitle}>Output schema (locked)</h2>
        <p className={styles.muted}>
          Formato: <code>{template.output_format}</code>
        </p>
        <ul className={styles.validationList}>
          {template.output_validation.map((v, i) => (
            <li key={i}>
              <code>{JSON.stringify(v)}</code>
            </li>
          ))}
        </ul>
      </section>

      <div className={styles.actions}>
        {statusMsg && <span className={styles.statusMsg}>{statusMsg}</span>}
        <Button
          variant="secondary"
          onClick={handleTest}
          loading={testMutation.isPending}
        >
          Testar com dados reais (~$0.05)
        </Button>
        <Button
          onClick={handleSaveVersion}
          loading={createVersionMutation.isPending}
        >
          Guardar nova versão
        </Button>
      </div>

      {testResult && (
        <section className={styles.card}>
          <h2 className={styles.sectionTitle}>Resultado do teste</h2>
          <div className={styles.meta}>
            <span>modelo: {testResult.model}</span>
            <span>
              tokens: {testResult.input_tokens} in / {testResult.output_tokens} out
            </span>
            <span>custo: ${testResult.cost_usd.toFixed(4)}</span>
          </div>
          <h3 className={styles.subTitle}>Validação</h3>
          <ul className={styles.validationList}>
            {testResult.validations.map((v, i) => (
              <li key={i} className={styles.valItem}>
                <StatusDot state={v.passed ? 'ok' : 'crit'} />
                <code>{v.check}</code>
                {v.detail && <span className={styles.muted}>— {v.detail}</span>}
              </li>
            ))}
          </ul>
          <h3 className={styles.subTitle}>Output</h3>
          <pre className={styles.code}>{testResult.output}</pre>
        </section>
      )}

      <section className={styles.card}>
        <h2 className={styles.sectionTitle}>Versões ({template.versions.length})</h2>
        <ul className={styles.versionList}>
          {template.versions.map((v) => (
            <li key={v.id} className={styles.versionRow}>
              <Badge tone={v.status === 'active' ? 'ok' : v.status === 'draft' ? 'warn' : 'neutral'} size="xs">
                v{v.version_number} · {v.status}
              </Badge>
              <span className={styles.muted}>
                {v.model} · temp {v.temperature}
              </span>
              <span className={styles.muted}>
                {new Date(v.created_at).toLocaleString('pt-BR')}
              </span>
              {v.status !== 'active' && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleActivate(v.id, v.version_number)}
                >
                  Activar
                </Button>
              )}
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}

interface ConfigInputProps {
  name: string;
  spec: ConfigurableField;
  value: unknown;
  onChange: (value: unknown) => void;
}

function ConfigurableInput({ name, spec, value, onChange }: ConfigInputProps) {
  if (spec.type === 'select') {
    return (
      <div className={styles.field}>
        <label className={styles.label}>{name}</label>
        <select
          value={(value as string) ?? spec.default ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={styles.select}
        >
          {spec.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (spec.type === 'number') {
    return (
      <div className={styles.field}>
        <label className={styles.label}>
          {name} {spec.min !== undefined && `(${spec.min}-${spec.max})`}
        </label>
        <input
          type="number"
          value={(value as number) ?? spec.default ?? 0}
          min={spec.min}
          max={spec.max}
          onChange={(e) => onChange(Number(e.target.value))}
          className={styles.input}
        />
      </div>
    );
  }

  if (spec.type === 'toggle') {
    return (
      <div className={styles.field}>
        <label className={styles.checkbox}>
          <input
            type="checkbox"
            checked={Boolean(value)}
            disabled={spec.locked}
            onChange={(e) => onChange(e.target.checked)}
          />
          {name} {spec.locked && <span className={styles.lockedTag}>locked</span>}
        </label>
      </div>
    );
  }

  if (spec.type === 'multi_toggle') {
    const arr = (value as string[]) ?? [];
    return (
      <div className={styles.field}>
        <label className={styles.label}>{name}</label>
        <div className={styles.multiToggle}>
          {spec.options.map((o) => (
            <label key={o.value} className={styles.checkbox}>
              <input
                type="checkbox"
                checked={arr.includes(o.value)}
                disabled={o.locked}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...arr, o.value]
                    : arr.filter((v) => v !== o.value);
                  onChange(next);
                }}
              />
              {o.label} {o.locked && <span className={styles.lockedTag}>locked</span>}
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (spec.type === 'text_list') {
    const arr = (value as string[]) ?? [];
    return (
      <div className={styles.field}>
        <label className={styles.label}>{name}</label>
        <input
          type="text"
          value={arr.join(', ')}
          placeholder={spec.placeholder ?? 'vírgula-separado'}
          onChange={(e) =>
            onChange(
              e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
          className={styles.input}
        />
      </div>
    );
  }

  return null;
}

export type { TemplateDetail };
